import React, { useState } from 'react';
import { Modal, Typography, Button, Tabs, Badge } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { Search, Link2, Globe, Bot, GraduationCap } from 'lucide-react';
import { WebsiteBrandingSection } from './onboardingData/WebsiteBrandingSection';
import { AIAssistantSection } from './onboardingData/AIAssistantSection';
import { IDXMLSSection } from './onboardingData/IDXMLSSection';
import { CRMSection } from './onboardingData/CRMSection';
import { CrashCourseSection } from './onboardingData/CrashCourseSection';
import { hasLegacyIdxData } from './onboardingData/legacyIdx';

interface OnboardingDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onRefresh?: () => void;
}

type TabValue = 'website' | 'ai' | 'idx' | 'crm' | 'crashCourse';

type Tab = { value: TabValue; label: string; icon: React.ReactNode; legacy?: boolean };

// Website holds data from the retired Website stage, so it sits last and is
// flagged Legacy. IDX/MLS is Legacy only when it holds the original IDX setup;
// a third-party IDX Display URL is current onboarding data.
const getTabs = (legacyIdx: boolean): Tab[] => {
  const idxTab: Tab = {
    value: 'idx',
    label: 'IDX/MLS',
    icon: <Search className="h-4 w-4" />,
    legacy: legacyIdx,
  };
  const websiteTab: Tab = {
    value: 'website',
    label: 'Website',
    icon: <Globe className="h-4 w-4" />,
    legacy: true,
  };
  return [
    { value: 'ai', label: 'AI Assistant', icon: <Bot className="h-4 w-4" /> },
    { value: 'crm', label: 'CRM', icon: <Link2 className="h-4 w-4" /> },
    { value: 'crashCourse', label: 'Support', icon: <GraduationCap className="h-4 w-4" /> },
    ...(legacyIdx ? [websiteTab, idxTab] : [idxTab, websiteTab]),
  ];
};

export const OnboardingDataModal: React.FC<OnboardingDataModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onRefresh,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabValue>('ai');

  if (!selectedOrg) return null;

  const onboardingSetup = selectedOrg.onboardingSetup || {};
  const onboardingData = selectedOrg.onboardingData || {};
  const tabs = getTabs(hasLegacyIdxData(onboardingData.idx));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <Typography variant="h3">Onboarding Data</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="h-full flex flex-col">
          <Tabs
            value={selectedTab}
            onChange={(value) => setSelectedTab(value as TabValue)}
            className="h-full flex flex-col"
          >
            <Tabs.List className="sticky top-0 bg-white dark:bg-transparent z-10">
              <div className="flex gap-2 md:gap-4 border-b border-gray-200 dark:border-white/10 overflow-x-auto scrollbar-custom">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    role="tab"
                    onClick={() => setSelectedTab(tab.value)}
                    className={`px-2 md:px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-lg flex items-center gap-2 ${
                      selectedTab === tab.value
                        ? 'bg-primary text-black'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                    {tab.legacy && (
                      <Badge size="sm" variant="secondary">
                        Legacy
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            </Tabs.List>

            <div className="flex-grow overflow-y-auto scrollbar-custom pt-4">
              {/* Website & Branding Tab */}
              <Tabs.Panel value="website">
                <WebsiteBrandingSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                  onRefresh={onRefresh}
                />
              </Tabs.Panel>

              {/* AI Assistant Tab */}
              <Tabs.Panel value="ai">
                <AIAssistantSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                />
              </Tabs.Panel>

              {/* IDX/MLS Tab */}
              <Tabs.Panel value="idx">
                <IDXMLSSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                />
              </Tabs.Panel>

              {/* CRM Tab */}
              <Tabs.Panel value="crm">
                <CRMSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                />
              </Tabs.Panel>

              {/* Support Tab (strategy call + support contact) */}
              <Tabs.Panel value="crashCourse">
                <CrashCourseSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                  onRefresh={onRefresh}
                />
              </Tabs.Panel>
            </div>
          </Tabs>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
